import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, MessageCircle, Home as HomeIcon, FileText, Clock, Users, Sparkles, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLang } from "../context/LanguageContext";
import { WHATSAPP_URL } from "../lib/i18n";
import { useEffect } from "react";

const STEP_ICONS = [CheckCircle2, Clock, Users, FileText];

export default function ThankYou() {
  const { t, isRTL } = useLang();
  const [params] = useSearchParams();
  const type = params.get("type") || "";
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const typedSubtitle = t.thankYou.typedSubtitles?.[type] || t.thankYou.subtitle;

  return (
    <div data-testid="thank-you-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0891b2] via-[#0891b2] to-[#0f172a] text-white py-24 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(220,38,38,0.3) 0%, transparent 40%), radial-gradient(circle at 80% 80%, rgba(59,130,246,0.3) 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-400 mb-6 animate-pulse">
            <CheckCircle2 className="w-10 h-10 text-green-400" />
          </div>
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] text-red-300 font-semibold mb-4 uppercase">
            <Sparkles className="w-3.5 h-3.5" />
            {t.thankYou.eyebrow}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.1] mb-4" data-testid="thank-you-title">
            {t.thankYou.title}
          </h1>
          <p className="text-cyan-100 text-lg leading-relaxed max-w-2xl mx-auto">{typedSubtitle}</p>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
              {t.thankYou.timelineEyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0891b2] leading-tight">{t.thankYou.timelineTitle}</h2>
          </div>

          <div className="relative">
            {/* Timeline connector line (desktop) */}
            <div className="hidden lg:block absolute top-16 left-[10%] right-[10%] h-px bg-slate-200" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.thankYou.steps.map((s, i) => {
                const Icon = STEP_ICONS[i] || CheckCircle2;
                return (
                  <div
                    key={i}
                    data-testid={`ty-step-${i}`}
                    className="relative bg-white rounded-2xl border border-slate-200 p-6 hover:shadow-lg transition-all group"
                  >
                    <div className="relative w-14 h-14 rounded-full bg-[#0891b2] group-hover:bg-[#ef4444] transition-colors flex items-center justify-center mx-auto mb-5 shadow-lg">
                      <Icon className="w-6 h-6 text-white" />
                      <div className="absolute -top-2 -end-2 w-6 h-6 rounded-full bg-white border-2 border-[#ef4444] text-[#ef4444] font-display text-xs flex items-center justify-center">
                        {i + 1}
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-[10px] tracking-widest text-slate-400 uppercase mb-1">{s.time}</div>
                      <h3 className="font-display text-lg text-[#0891b2] mb-2 leading-snug">{s.title}</h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{s.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reassurance box */}
          <div className="mt-12 bg-gradient-to-r from-cyan-50 to-red-50/30 border border-cyan-100 rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start sm:items-center">
            <div className="w-14 h-14 rounded-full bg-white shadow-md flex items-center justify-center shrink-0">
              <FileText className="w-6 h-6 text-[#ef4444]" />
            </div>
            <div className="flex-1">
              <h3 className="font-display text-lg text-[#0891b2] mb-1">{t.thankYou.refTitle}</h3>
              <p className="text-slate-600 text-sm">{t.thankYou.refText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Actions */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-2xl sm:text-3xl text-[#0891b2] mb-3">{t.thankYou.actionsTitle}</h2>
          <p className="text-slate-600 mb-8">{t.thankYou.actionsSubtitle}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer">
              <Button
                data-testid="ty-whatsapp"
                className="bg-green-600 hover:bg-green-700 text-white h-12 px-6 font-semibold shadow-lg"
              >
                <MessageCircle className="w-4 h-4 me-2" /> {t.thankYou.btnWhatsapp}
              </Button>
            </a>
            <Link to="/projects">
              <Button
                variant="outline"
                data-testid="ty-projects"
                className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white h-12 px-6 font-semibold"
              >
                {t.thankYou.btnProjects} <Arrow className="w-4 h-4 ms-2" />
              </Button>
            </Link>
            <Link to="/">
              <Button
                variant="outline"
                data-testid="ty-home"
                className="border-slate-300 text-slate-700 hover:bg-slate-100 h-12 px-6 font-semibold"
              >
                <HomeIcon className="w-4 h-4 me-2" /> {t.thankYou.btnHome}
              </Button>
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-slate-500 text-xs tracking-widest uppercase">
            <span dir="ltr">+971 4-288-2777</span>
            <span className="hidden sm:inline">·</span>
            <span dir="ltr">admin@mariotkitchen.com</span>
          </div>
        </div>
      </section>
    </div>
  );
}
